/**
 * Beam tracer.
 *
 * Model: a beam is a light ray following the ray equation of geometric optics,
 *
 *     d/ds (n t) = grad n,   |t| = 1,
 *
 * in an effective refractive index n(x) = exp( sum_i mu_i / r_i ), where r_i is the distance to
 * mass i and mu_i = 2 G M_i (game units, c = 1). Expanding gives the curvature
 *
 *     dt/ds = g - (g . t) t,   g = grad ln n = sum_i mu_i (c_i - x) / r_i^3,
 *
 * i.e. a pull towards every mass that falls off as M / r^2, applied perpendicular to the beam
 * (light keeps its speed). For one mass this yields the weak-field deflection 2 mu / b = 4 G M / b,
 * the same form as Einstein's 4GM / (c^2 b). For one mass the Bouguer invariant n r sin(psi) is
 * conserved, and rays with impact parameter below e * mu fall inside the photon sphere r = mu.
 *
 * Integration: classic RK4 in arc length with renormalised direction, step size refined near
 * masses (turning angle per step and distance to the nearest mass are both bounded). Collisions
 * are tested exactly against the straight chord of each step. Everything is deterministic: the same
 * scene and start always give bit-identical output.
 */

export type EndReason = 'exit' | 'receiver' | 'blocked' | 'absorbed' | 'captured' | 'limit';

/** What a terminating circle does to a beam. */
export type CircleRole = 'receiver' | 'blocked' | 'absorbed' | 'captured';

export interface SceneCircle {
  x: number;
  y: number;
  r: number;
  role: CircleRole;
  /** Index of the receiver (role receiver) or of the mass (role captured / mass body), else -1. */
  ref: number;
}

export interface SceneWall {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  half: number;
}

export interface SceneNebula {
  x: number;
  y: number;
  r: number;
  color: number;
}

export interface Scene {
  /** Packed masses: x, y, mu for each mass. */
  masses: Float64Array;
  massCount: number;
  circles: SceneCircle[];
  walls: SceneWall[];
  nebulae: SceneNebula[];
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface BeamRun {
  color: number;
  /** Flat list of points: x0, y0, x1, y1, ... */
  pts: number[];
}

export interface BeamTrace {
  runs: BeamRun[];
  reason: EndReason;
  /** Receiver index (reason receiver), mass index (reason captured), otherwise -1. */
  target: number;
  /** Colour on arrival (0 when absorbed by a nebula). */
  color: number;
  x: number;
  y: number;
  /** Direction of travel at the end. */
  tx: number;
  ty: number;
  length: number;
  steps: number;
}

export interface TraceOptions {
  /** Longest path before giving up (beams can orbit a black hole). */
  maxLength?: number;
  maxSteps?: number;
  /** Largest step, used far from masses. */
  hMax?: number;
  /** Smallest step. */
  hMin?: number;
  /** Largest turning angle per step, radians. */
  turn?: number;
  /** Record path points (turn off for fast searches that only need the outcome). */
  record?: boolean;
  /** Called after every accepted step with position and unit direction (diagnostics, tests). */
  probe?: ((x: number, y: number, tx: number, ty: number) => void) | null;
}

const DEFAULTS: Required<TraceOptions> = {
  maxLength: 6000,
  maxSteps: 30000,
  hMax: 10,
  hMin: 0.02,
  turn: 0.025,
  record: true,
  probe: null,
};

// Scratch output of the field evaluation (avoids allocating in the inner loop).
let gx = 0;
let gy = 0;

/** Field g = grad ln n at (px, py). Returns sum |mu| / r^2 (curvature scale) and sets gx, gy. */
function field(m: Float64Array, n: number, px: number, py: number): number {
  let ax = 0;
  let ay = 0;
  let k = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 3;
    const dx = m[o]! - px;
    const dy = m[o + 1]! - py;
    const mu = m[o + 2]!;
    const r2 = dx * dx + dy * dy;
    const inv = 1 / Math.sqrt(r2);
    const f = mu * inv * inv * inv;
    ax += f * dx;
    ay += f * dy;
    k += Math.abs(mu) / r2;
  }
  gx = ax;
  gy = ay;
  return k;
}

/** Distance to the nearest mass centre (used to bound the step). */
function nearest(m: Float64Array, n: number, px: number, py: number): number {
  let best = Infinity;
  for (let i = 0; i < n; i++) {
    const o = i * 3;
    const dx = m[o]! - px;
    const dy = m[o + 1]! - py;
    const r2 = dx * dx + dy * dy;
    if (r2 < best) best = r2;
  }
  return Math.sqrt(best);
}

/**
 * First parameter t in [0, 1] where the chord p0 + t v enters the circle, or Infinity.
 * A chord that starts inside counts as an immediate hit (t = 0).
 */
export function circleEntry(
  p0x: number,
  p0y: number,
  vx: number,
  vy: number,
  cx: number,
  cy: number,
  r: number,
): number {
  const fx = p0x - cx;
  const fy = p0y - cy;
  const c = fx * fx + fy * fy - r * r;
  if (c <= 0) return 0;
  const b = fx * vx + fy * vy;
  if (b >= 0) return Infinity;
  const vv = vx * vx + vy * vy;
  const disc = b * b - vv * c;
  if (disc < 0) return Infinity;
  const t = (-b - Math.sqrt(disc)) / vv;
  return t <= 1 ? t : Infinity;
}

/** First parameter t in [0, 1] where the chord enters a capsule-shaped wall, or Infinity. */
export function wallEntry(p0x: number, p0y: number, vx: number, vy: number, w: SceneWall): number {
  const ex = w.x2 - w.x1;
  const ey = w.y2 - w.y1;
  const len = Math.sqrt(ex * ex + ey * ey);
  const capA = circleEntry(p0x, p0y, vx, vy, w.x1, w.y1, w.half);
  const capB = circleEntry(p0x, p0y, vx, vy, w.x2, w.y2, w.half);
  let best = capA < capB ? capA : capB;
  if (len < 1e-9) return best;
  const ux = ex / len;
  const uy = ey / len;
  const rx = p0x - w.x1;
  const ry = p0y - w.y1;
  const u0 = rx * ux + ry * uy;
  const v0 = -rx * uy + ry * ux;
  const du = vx * ux + vy * uy;
  const dv = -vx * uy + vy * ux;
  let tIn = 0;
  let tOut = 1;
  // Slab along the wall: 0 <= u <= len.
  if (Math.abs(du) < 1e-12) {
    if (u0 < 0 || u0 > len) return best;
  } else {
    let a = -u0 / du;
    let b = (len - u0) / du;
    if (a > b) [a, b] = [b, a];
    if (a > tIn) tIn = a;
    if (b < tOut) tOut = b;
  }
  // Slab across the wall: -half <= v <= half.
  if (Math.abs(dv) < 1e-12) {
    if (v0 < -w.half || v0 > w.half) return best;
  } else {
    let a = (-w.half - v0) / dv;
    let b = (w.half - v0) / dv;
    if (a > b) [a, b] = [b, a];
    if (a > tIn) tIn = a;
    if (b < tOut) tOut = b;
  }
  if (tIn <= tOut && tIn < best) best = tIn;
  return best;
}

/**
 * Trace one beam from (x, y) in direction (dx, dy) (need not be normalised) with colour `color`.
 */
export function traceBeam(
  scene: Scene,
  x: number,
  y: number,
  dx: number,
  dy: number,
  color: number,
  options: TraceOptions = {},
): BeamTrace {
  const o = { ...DEFAULTS, ...options };
  const m = scene.masses;
  const nm = scene.massCount;
  const circles = scene.circles;
  const walls = scene.walls;
  const nebulae = scene.nebulae;

  let dl = Math.sqrt(dx * dx + dy * dy);
  let tx = dx / dl;
  let ty = dy / dl;
  let px = x;
  let py = y;

  // A beam that starts inside a nebula is filtered from the start.
  for (const nb of nebulae) {
    const ex = px - nb.x;
    const ey = py - nb.y;
    if (ex * ex + ey * ey <= nb.r * nb.r) color &= nb.color;
  }

  const runs: BeamRun[] = [];
  let run: BeamRun = { color, pts: o.record ? [px, py] : [] };
  runs.push(run);

  const finish = (
    reason: EndReason,
    target: number,
    ex: number,
    ey: number,
    len: number,
    steps: number,
    dirX = tx,
    dirY = ty,
  ): BeamTrace => {
    if (o.record) run.pts.push(ex, ey);
    return {
      runs,
      reason,
      target,
      color: reason === 'absorbed' ? 0 : color,
      x: ex,
      y: ey,
      tx: dirX,
      ty: dirY,
      length: len,
      steps,
    };
  };
  /** Direction part-way (t in [0, 1]) through the current step, from its two end directions. */
  const between = (t: number, ax: number, ay: number, bx: number, by: number): [number, number] => {
    const ix = ax + t * (bx - ax);
    const iy = ay + t * (by - ay);
    const il = Math.sqrt(ix * ix + iy * iy);
    return [ix / il, iy / il];
  };

  if (color === 0) return finish('absorbed', -1, px, py, 0, 0);

  // Outside the plate already (a malformed level): nothing to trace.
  if (px < scene.x0 || px > scene.x1 || py < scene.y0 || py > scene.y1) {
    return finish('exit', -1, px, py, 0, 0);
  }

  let length = 0;
  let steps = 0;
  const neb = new Array<{ t: number; c: number }>();

  while (true) {
    if (steps >= o.maxSteps || length >= o.maxLength) {
      return finish('limit', -1, px, py, length, steps);
    }
    steps++;

    // ---- choose the step from the local field ----
    let h = o.hMax;
    let k1x = 0;
    let k1y = 0;
    if (nm > 0) {
      const kappa = field(m, nm, px, py);
      k1x = gx;
      k1y = gy;
      if (kappa * h > o.turn) h = o.turn / kappa;
      const near = nearest(m, nm, px, py) * 0.25;
      if (h > near) h = near;
      if (h < o.hMin) h = o.hMin;
    }

    // ---- RK4 on (p, t) ----
    let nx: number;
    let ny: number;
    let ntx: number;
    let nty: number;
    if (nm === 0) {
      nx = px + h * tx;
      ny = py + h * ty;
      ntx = tx;
      nty = ty;
    } else {
      // stage 1
      const a1 = k1x * tx + k1y * ty;
      const d1x = k1x - a1 * tx;
      const d1y = k1y - a1 * ty;
      // stage 2
      const h2 = h * 0.5;
      let sx = tx + h2 * d1x;
      let sy = ty + h2 * d1y;
      field(m, nm, px + h2 * tx, py + h2 * ty);
      let ss = sx * sx + sy * sy;
      let a = (gx * sx + gy * sy) / ss;
      const d2x = gx - a * sx;
      const d2y = gy - a * sy;
      const v2x = sx;
      const v2y = sy;
      // stage 3
      sx = tx + h2 * d2x;
      sy = ty + h2 * d2y;
      field(m, nm, px + h2 * v2x, py + h2 * v2y);
      ss = sx * sx + sy * sy;
      a = (gx * sx + gy * sy) / ss;
      const d3x = gx - a * sx;
      const d3y = gy - a * sy;
      const v3x = sx;
      const v3y = sy;
      // stage 4
      sx = tx + h * d3x;
      sy = ty + h * d3y;
      field(m, nm, px + h * v3x, py + h * v3y);
      ss = sx * sx + sy * sy;
      a = (gx * sx + gy * sy) / ss;
      const d4x = gx - a * sx;
      const d4y = gy - a * sy;
      const v4x = sx;
      const v4y = sy;

      const h6 = h / 6;
      nx = px + h6 * (tx + 2 * v2x + 2 * v3x + v4x);
      ny = py + h6 * (ty + 2 * v2y + 2 * v3y + v4y);
      ntx = tx + h6 * (d1x + 2 * d2x + 2 * d3x + d4x);
      nty = ty + h6 * (d1y + 2 * d2y + 2 * d3y + d4y);
      dl = Math.sqrt(ntx * ntx + nty * nty);
      ntx /= dl;
      nty /= dl;
    }

    // ---- collisions along the chord ----
    const vx = nx - px;
    const vy = ny - py;
    let tHit = Infinity;
    let hitReason: EndReason = 'exit';
    let hitTarget = -1;

    if (nx < scene.x0 || nx > scene.x1 || ny < scene.y0 || ny > scene.y1) {
      let t = Infinity;
      if (nx < scene.x0) t = Math.min(t, (scene.x0 - px) / vx);
      if (nx > scene.x1) t = Math.min(t, (scene.x1 - px) / vx);
      if (ny < scene.y0) t = Math.min(t, (scene.y0 - py) / vy);
      if (ny > scene.y1) t = Math.min(t, (scene.y1 - py) / vy);
      tHit = Math.max(0, t);
    }
    for (let i = 0; i < circles.length; i++) {
      const c = circles[i]!;
      const t = circleEntry(px, py, vx, vy, c.x, c.y, c.r);
      if (t < tHit) {
        tHit = t;
        hitReason = c.role;
        hitTarget = c.role === 'receiver' || c.role === 'captured' ? c.ref : -1;
      }
    }
    for (let i = 0; i < walls.length; i++) {
      const t = wallEntry(px, py, vx, vy, walls[i]!);
      if (t < tHit) {
        tHit = t;
        hitReason = 'blocked';
        hitTarget = -1;
      }
    }

    // Nebula entries before the hit recolour the beam (filter = intersection).
    if (nebulae.length > 0) {
      neb.length = 0;
      for (const nb of nebulae) {
        const ex = px - nb.x;
        const ey = py - nb.y;
        if (ex * ex + ey * ey <= nb.r * nb.r) continue; // already inside, already filtered
        const t = circleEntry(px, py, vx, vy, nb.x, nb.y, nb.r);
        if (t <= 1 && t < tHit) neb.push({ t, c: nb.color });
      }
      if (neb.length > 1) neb.sort((p, q) => p.t - q.t);
      for (const e of neb) {
        const next = color & e.c;
        if (next === color) continue;
        const ex = px + e.t * vx;
        const ey = py + e.t * vy;
        if (next === 0) {
          const [dx2, dy2] = between(e.t, tx, ty, ntx, nty);
          return finish('absorbed', -1, ex, ey, length + e.t * h, steps, dx2, dy2);
        }
        if (o.record) run.pts.push(ex, ey);
        color = next;
        run = { color, pts: o.record ? [ex, ey] : [] };
        runs.push(run);
      }
    }

    if (tHit <= 1) {
      const [dx2, dy2] = between(tHit, tx, ty, ntx, nty);
      const ex = px + tHit * vx;
      const ey = py + tHit * vy;
      return finish(hitReason, hitTarget, ex, ey, length + tHit * h, steps, dx2, dy2);
    }

    px = nx;
    py = ny;
    tx = ntx;
    ty = nty;
    length += h;
    if (o.record) run.pts.push(px, py);
    if (o.probe) o.probe(px, py, tx, ty);
  }
}
